// Audit Tax Computation Service
// Corresponds to org.zhuo_yi.audit.controller.AuditTaxComController

import { BASE_AUDIT_API, authorizedFetch, fetchAPI, getHttpErrorMessage } from '@/services/api';
import {
    TaxComputationCombinedVO,
    AuditTaxComBO
} from '@/services/auditService/interface/tax-com';

const base = `${BASE_AUDIT_API}/tax-com`;

export interface FileDownloadResult {
    blob: Blob;
    filename?: string;
    contentType?: string;
}

function tryParseFilenameFromContentDisposition(contentDisposition: string | null): string | undefined {
    if (!contentDisposition) return undefined;

    const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(contentDisposition);
    const raw = match?.[1] || match?.[2];
    if (!raw) return undefined;

    try {
        return decodeURIComponent(raw.replace(/\+/g, '%20'));
    } catch {
        return raw.replace(/\+/g, ' ');
    }
}

async function fetchFile(path: string, options?: RequestInit): Promise<FileDownloadResult> {
    // Must use authorizedFetch so path is ciphered when payload cipher is on.
    // Binary Excel responses stay unencrypted (CryptoPayloadFilter only wraps JSON).
    const res = await authorizedFetch(path, options);

    if (!res.ok) {
        throw new Error(await getHttpErrorMessage(res, 'File download error'));
    }

    // Gateway crypto errors often return HTTP 200 + JSON Result (e.g. code 4605).
    const contentType = res.headers.get('content-type') || undefined;
    if ((contentType || '').toLowerCase().includes('json')) {
        throw new Error(await getHttpErrorMessage(res, 'File download error'));
    }

    const filename = tryParseFilenameFromContentDisposition(res.headers.get('content-disposition'));
    const blob = await res.blob();

    return { blob, filename, contentType };
}

/**
 * Query Combined Tax Computation - Main + P1.1/P1.2/P1.21/P1.3 in one call
 *
 * 后端规则：
 * - 主结果 taxComputationResult 始终返回；
 * - 当公司 principalActivities == 0（Inactive）时，子附表 p11/p12/p121/p13 均为 null；
 * - 当对应子附表查询结果不含任何数据时，该字段为 null。
 *
 * @param companyId Company ID
 * @param annualId  Annual ID
 * @param yearOfAssessment Optional YOA slice (empty = legacy/SINGLE)
 */
export async function queryTaxCombined(companyId: number, annualId: number, yearOfAssessment?: string) {
    const params = new URLSearchParams({
        companyId: String(companyId),
        annualId: String(annualId),
    });
    const yoa = (yearOfAssessment ?? '').trim();
    if (yoa) {
        params.set('yearOfAssessment', yoa);
    }
    return fetchAPI<TaxComputationCombinedVO>(`${base}/queryTaxCombined?${params.toString()}`);
}

/**
 * Save or Update Tax Computation Data
 * 
 * @param data AuditTaxComBO request body
 * @returns boolean indicating success
 */
export async function saveOrUpdate(data: AuditTaxComBO) {
    return fetchAPI<boolean>(`${base}/saveOrUpdate`, {
        method: 'POST',
        body: JSON.stringify(data),
    });
}

/**
 * Export Tax Computation Excel (P1a/P1b/P1c + P1.1/P1.2/P1.21/P1.3)
 * MULTI：后端按全部 YOA 分 sheet 导出（P1 2024 / P1 2025 / …），无需传 yearOfAssessment。
 * GET /tax-com/export-taxCom
 */
export async function exportTaxComExcel(companyId: number, annualId: number, yearOfAssessment?: string) {
    const params = new URLSearchParams({
        companyId: String(companyId),
        annualId: String(annualId),
    });
    // SINGLE 兼容仍可带 YOA；MULTI 时后端会忽略并导出全部 YOA
    const yoa = (yearOfAssessment ?? '').trim();
    if (yoa) {
        params.set('yearOfAssessment', yoa);
    }
    return fetchFile(`${base}/export-taxCom?${params.toString()}`, {
        method: 'GET',
    });
}

/**
 * Export PTR Excel
 * POST /tax-com/export-taxReturn
 */
export async function exportTaxReturnExcel(companyId: number, annualId: number, yearOfAssessment?: string) {
    const params = new URLSearchParams({
        companyId: String(companyId),
        annualId: String(annualId),
    });
    const yoa = (yearOfAssessment ?? '').trim();
    if (yoa) {
        params.set('yearOfAssessment', yoa);
    }
    return fetchFile(`${base}/export-taxReturn?${params.toString()}`, {
        method: 'POST',
    });
}

// Export all functions as a service object
export const auditTaxComService = {
    queryTaxCombined,
    saveOrUpdate,
    exportTaxComExcel,
    exportTaxReturnExcel,
};

// Export types from interface file
export type {
    TaxComputationCombinedVO,
    TaxComputationResult,
    TaxDepreciationAllowanceScheduleVO,
    TaxAdditionsNonCurrentAssetsVO,
    TaxObligationUnderFinanceLeasesVO,
    TaxPpeDetailsVO,
    AuditTaxComBO,
    ProfitsTaxAVO,
    ProfitsTaxBVO,
    ProfitsTaxCVO,
    TaxBasicProfitsVO,
    TaxBalancingAllowance,
    TaxCommercialBuilding,
    TaxMotorVehicle,
    TaxMotorHirePurchase,
    TaxNonCurrentAsset,
    TaxPpe,
    TaxCostPerBook,
    AuditTaxComPropertyBO,
} from '@/services/auditService/interface/tax-com';

export { TaxComputationType } from '@/services/auditService/interface/tax-com';
